import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useActivation } from './ActivationContext';
import { AuthContext } from 'components/lib';
import CustomButton from 'components/custom/customButton';
import {
  UilRocket,
  UilCheck,
  UilTimes,
  UilClock,
} from '@iconscout/react-unicons';
import { capture } from '../../analytics/posthog';
import './ActivationWidget.scss';

export default function ActivationWidget() {
  const {
    activationData,
    isOpen,
    toggleOpen,
    close,
    dismissCelebration,
    dismissWidget,
  } = useActivation();
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);

  // Hidden for client-permission users, on auth/onboarding pages so the widget
  // never renders before the dashboard
  if (
    authContext?.user?.permission === 'client' ||
    location?.pathname?.includes('/verification') ||
    location?.pathname?.includes('/magic') ||
    location?.pathname?.includes('/signin') ||
    location?.pathname?.includes('/signup') ||
    location?.pathname?.includes('/onboarding')
  ) {
    return null;
  }

  // If account is not eligible, user has completed all and dismissed, or the
  // widget was permanently dismissed for the account tree, render nothing
  if (!activationData || !activationData.eligible || activationData.completed_seen || activationData.widget_dismissed) {
    return null;
  }

  const {
    steps = [],
    bonus_days_earned = 0,
    total_bonus_available = 13,
    days_left = 0,
    completed_all = false,
    days_rewards = true,
  } = activationData;

  const completedCount = steps.filter((s) => s.done).length;
  const progressPercent = days_rewards
    ? Math.min(100, Math.round((bonus_days_earned / total_bonus_available) * 100))
    : Math.min(100, Math.round((completedCount / (steps.length || 1)) * 100));

  const handleStepClick = (step) => {
    capture('activation_step_clicked', {
      step: step.key,
      surface: 'floating_widget',
    });
    close();
    navigate(step.cta);
  };

  const handleDismissRequest = () => {
    setConfirmingDismiss(true);
  };

  const handleDismissConfirm = async () => {
    capture('activation_widget_dismissed', { surface: 'floating_widget' });
    await dismissWidget();
  };

  return (
    <>
      {/* 1. Floating Launcher Button (Bottom-Left) */}
      {!isOpen && !confirmingDismiss && (
        <div className="activation-launcher-wrap">
          <button
            className="launcher-dismiss-btn"
            onClick={handleDismissRequest}
            aria-label="Dismiss setup widget"
            title="Dismiss"
          >
            <UilTimes size={14} />
          </button>
          <button
            className="activation-launcher-btn"
            onClick={toggleOpen}
            aria-label="Open Trial Activation Ladder"
          >
            <div className="launcher-icon">
              <UilRocket size={20} />
            </div>
            <span>
              {completed_all
                ? days_rewards
                  ? 'Rewards Unlocked'
                  : 'Setup Complete'
                : days_rewards
                  ? 'Unlock +13 Days'
                  : 'Complete Your Setup'}
            </span>
            <span className="launcher-badge">
              {completedCount}/{steps.length}
            </span>
          </button>
        </div>
      )}

      {/* 1b. Dismiss confirmation — replaces the launcher until decided */}
      {!isOpen && confirmingDismiss && (
        <div className="activation-dismiss-confirm">
          <span className="dismiss-confirm-text">Are you sure? This would not be shown again.</span>
          <button
            className="dismiss-confirm-btn cancel"
            onClick={() => setConfirmingDismiss(false)}
            aria-label="Cancel dismissal"
            title="Cancel"
          >
            <UilTimes size={15} />
          </button>
          <button
            className="dismiss-confirm-btn confirm"
            onClick={handleDismissConfirm}
            aria-label="Confirm dismissal"
            title="Don't show again"
          >
            <UilCheck size={15} />
          </button>
        </div>
      )}

      {/* 2. Expanded Flyout Card (Crisp Style matching screenshot) */}
      {isOpen && (
        <div className="activation-flyout-overlay" onClick={close}>
          <div
            className="activation-flyout-card csCard"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="activation-header">
              <div className="header-top">
                <h3>
                  <UilRocket size={22} color="#0066ff" />
                  {days_rewards ? 'Trial Activation Rewards' : 'Account Activation'}
                </h3>
                <button className="close-btn" onClick={close} aria-label="Close">
                  <UilTimes size={20} />
                </button>
              </div>
              <p className="header-desc">
                {days_rewards
                  ? 'Complete these 4 milestones to earn up to 13 extra trial days.'
                  : 'Complete these 4 milestones to activate your account and get the most out of SalesBlink.'}
              </p>

              <div className="csProgressBar success" style={{ width: '100%' }}>
                <div style={{ width: `${progressPercent}%` }}></div>
              </div>

              <div className="progress-meta">
                <span className="earned-count">
                  {days_rewards
                    ? `${bonus_days_earned} of ${total_bonus_available} bonus days earned (${completedCount}/${steps.length} done)`
                    : `${completedCount}/${steps.length} milestones completed`}
                </span>
                {days_rewards && (
                  <span className="days-left-count">
                    {days_left} {days_left === 1 ? 'day' : 'days'} left in trial
                  </span>
                )}
              </div>
            </div>

            {/* Checklist items matching screenshot */}
            <div className="activation-list">
              {steps.map((step) => {
                return (
                  <div
                    key={step.key}
                    className={`step-row ${step.done ? 'step-done' : ''}`}
                  >
                    <div className="step-left">
                      <div className="step-icon">
                        {step.done ? (
                          <div className="icon-circle-done">
                            <UilCheck size={16} />
                          </div>
                        ) : (
                          <div className="icon-circle-pending" />
                        )}
                      </div>
                      <div className="step-info">
                        <span className="step-title">{step.title}</span>
                        <span className="step-desc">{step.description}</span>
                      </div>
                    </div>

                    <div className="step-right">
                      {days_rewards && (
                        <span
                          className={`csBadge csCenter flex items-center gap-1 whitespace-nowrap ${
                            step.done ? 'success' : 'warning'
                          }`}
                          style={{ fontSize: 13, fontWeight: 700, padding: '3px 12px' }}
                        >
                          {step.done ? (
                            <UilCheck size={13} />
                          ) : (
                            <UilClock size={13} />
                          )}
                          +{step.days} {step.days === 1 ? 'day' : 'days'}
                        </span>
                      )}

                      {step.done ? (
                        <CustomButton
                          variant="secondary"
                          title="Open"
                          onClick={() => handleStepClick(step)}
                          style={{ marginTop: 0, padding: '4px 12px', fontSize: 12 }}
                        />
                      ) : (
                        <CustomButton
                          variant="primary"
                          title="Get Started"
                          onClick={() => handleStepClick(step)}
                          style={{ marginTop: 0, padding: '4px 12px', fontSize: 12 }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Celebration State when all 4 steps completed */}
            {completed_all && (
              <div className="activation-celebration">
                <div className="celebration-text">
                  <h4>🎉 {days_rewards ? 'All milestones unlocked!' : 'All milestones complete!'}</h4>
                  <p>
                    {days_rewards
                      ? 'You have earned all 13 bonus trial days.'
                      : 'Your account is fully set up and ready to go.'}
                  </p>
                </div>
                <CustomButton
                  variant="primary green"
                  title="Got It"
                  onClick={dismissCelebration}
                  style={{ marginTop: 0, padding: '4px 12px', fontSize: 12 }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
